import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Bar, CartesianGrid, ComposedChart, LabelList, Line, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import ReportChartTooltip from '~/components/Common/ReportChartTooltip';

import FinancialActions from './FinancialActions';
import FinancialMatrix from './FinancialMatrix';
import {
  type CMPageContext,
  type ReportState,
  type ReportView,
  comparisonWindow,
  currentWindow,
  focusMarkets,
  formatMetric,
  metricsFor,
  reportMarkets,
  reportViews,
  selectionComparisons,
} from './fidelity';
import './fidelity.css';
import { useContributionModel } from './presentationContext';
import { type Value, number, percent } from './presentationModel';

export type ContributionMarginReportProps = {
  queryPrefix?: string;
  nativeTheme?: 'light' | 'dark';
  onContextChange?: (context: CMPageContext) => void;
  onAskRichard?: (context: CMPageContext) => void;
  state: ReportState;
  onChange: (state: ReportState) => void;
};
type Inspection = { title: string; rows: Array<[string, string]> };
const displayMoney = (v: Value) => (v === null ? 'Unknown' : 'A$' + number(v));
/** The observed MTD window behind the linear expectation, from the source-through date. */
const mtdNote = (through: string) => {
  const [year, month, day] = through.split('-').map(Number);
  if (!year || !month || !day) return 'Expectation = observed month-to-date amount / days observed × days in month.';
  const days = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const name = new Date(Date.UTC(year, month - 1, 1)).toLocaleString('en-AU', { month: 'long', timeZone: 'UTC' });
  return `Expectation = observed 1–${day} ${name} amount / ${day} × ${days}.`;
};
const viewTitles: Record<ReportView, string> = {
  'Daily CM Tracking': 'Contribution Margin Tracker',
  'Global Monthly CM Tracking': 'Contribution Margin Tracker',
  'Regions Monthly CM Tracking': 'Contribution Margin Tracker',
  'Comparision Overview': 'Comparison | Overview',
  'Comparision Chart': 'Comparison | Chart',
  'MTD Expectation': 'Contribution Margin Tracker | MTD Expectation',
  'Source health': 'Contribution Margin Tracker | Source health',
  'Sheet 68 · legacy bank fees': 'Contribution Margin Tracker | Sheet 68',
};
const dateLabel = (s: string) =>
  new Date(s + 'T00:00:00Z').toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
function SelectField({
  label,
  value,
  values,
  onChange,
}: {
  label: string;
  value: string;
  values: Array<string>;
  onChange: (value: string) => void;
}) {
  return (
    <label className="cm-field">
      <span>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        {values.map((v) => (
          <option key={v}>{v}</option>
        ))}
      </select>
    </label>
  );
}
function Growth({ value }: { value: Value }) {
  return (
    <span className={'cm-change ' + (value === null ? 'unknown' : value >= 0 ? 'increase' : 'decrease')}>
      {value === null ? 'Unknown' : `${value >= 0 ? '▲ +' : '▼ '}${percent(value)}`}
    </span>
  );
}
function Matrix({
  state,
  market,
  onInspect,
  projection = false,
}: {
  state: ReportState;
  market: string;
  onInspect: (x: Inspection) => void;
  projection?: boolean;
}) {
  const { projected, matrixColumns, metadata } = useContributionModel();
  const columns = useMemo(
    () =>
      projection ? [{ date: 'Month expectation', metrics: projected(state, market) }] : matrixColumns(state, market),
    [state, market, projection, matrixColumns, projected],
  );
  const metrics = metricsFor(state.view);
  return (
    <section className="cm-matrix-section">
      <h2>
        {projection
          ? market === 'Global - All Markets'
            ? 'Global Current MTD Expectation'
            : market
          : state.view === 'Daily CM Tracking'
            ? `Daily ${market}`
            : state.view === 'Regions Monthly CM Tracking'
              ? market
              : 'Global Monthly'}
      </h2>
      {(state.view === 'Global Monthly CM Tracking' || (projection && market === 'Global - All Markets')) && (
        <div className="cm-bank-alerts" aria-label="Bank fee alerts">
          {columns.map((c) => (
            <span key={c.date} className={(c.metrics.bank ?? 0) >= 1000000 ? 'above' : 'below'}>
              {c.date}:{' '}
              {c.metrics.bank === null ? 'Unknown' : c.metrics.bank >= 1000000 ? 'Bank fees above 1M' : 'Bank fees ok'}
            </span>
          ))}
        </div>
      )}
      <div className="cm-matrix-scroll" role="region" aria-label={`${market} ${state.view} matrix`} tabIndex={0}>
        <table className="cm-matrix">
          <thead>
            <tr>
              <th scope="col">Amounts A$ · % where labelled</th>
              {columns.map((c) => (
                <th scope="col" key={c.date}>
                  {c.date === 'Month expectation'
                    ? c.date
                    : state.view === 'Daily CM Tracking'
                      ? dateLabel(c.date)
                      : new Date(c.date + '-01T00:00:00Z').toLocaleDateString('en-AU', {
                          month: 'short',
                          year: 'numeric',
                          timeZone: 'UTC',
                        })}
                  {state.view === 'Daily CM Tracking' && (
                    <small>
                      {new Date(c.date + 'T00:00:00Z').toLocaleDateString('en-AU', {
                        weekday: 'long',
                        timeZone: 'UTC',
                      })}
                    </small>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {metrics.map((m) => {
              const max = Math.max(1, ...columns.map((c) => Math.abs(c.metrics[m.key] ?? 0)));
              return (
                <tr key={m.key} className={'group-' + m.group + (m.start ? ' group-start' : '')}>
                  <th scope="row">{m.label}</th>
                  {columns.map((c) => {
                    const val = c.metrics[m.key] ?? null;
                    const noSourceRow = val === null && m.group === 'marketing';
                    const intensity = val === null ? 0 : Math.min(1, Math.abs(val) / max);
                    return (
                      <td
                        key={c.date}
                        data-metric={m.key}
                        className={val === null ? 'is-unknown' : ''}
                        style={{ '--heat': `${Math.round(8 + intensity * 18)}%` } as React.CSSProperties}
                      >
                        <button
                          data-lens-pdf-keep
                          aria-label={`${m.label}, ${c.date}, ${noSourceRow ? 'no source row' : formatMetric(val, m.format)}`}
                          onClick={() =>
                            onInspect({
                              title: m.label,
                              rows: [
                                ['Market', market],
                                ['Period', c.date],
                                ['Value', formatMetric(val, m.format, 2)],
                                [
                                  'Type',
                                  m.key === 'target'
                                    ? 'Full-month source target'
                                    : projection
                                      ? 'Straight-line month expectation'
                                      : 'Observed source aggregate',
                                ],
                                [
                                  'Source',
                                  m.key === 'target'
                                    ? 'Finance FY27 CM Budget (Group tab CM Target), FY25-FY26 as in Tableau cm_targets'
                                    : metadata.source,
                                ],
                                ['Source through', metadata.to],
                                [
                                  'Definition',
                                  m.key === 'net'
                                    ? 'Net CM includes interest income and VCC rebate.'
                                    : m.key === 'target'
                                      ? 'Full-month target from the Finance FY27 CM Budget (Group tab CM Target, as in Tableau). Global excludes the separate US/EU/UK rows; regional targets use exact source region matches. Missing source months/regions read Unknown.'
                                      : 'Workbook revision 7.1 metric.',
                                ],
                              ],
                            })
                          }
                        >
                          {noSourceRow
                            ? '–'
                            : formatMetric(
                                val,
                                m.format,
                                m.format === 'money' || (m.format === 'percent' && state.view !== 'Daily CM Tracking') ? 2 : 0,
                              )}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
        {metrics.some((m) => m.group === 'marketing' && columns.some((c) => (c.metrics[m.key] ?? null) === null)) && (
          <p className="cm-small-note">– = no source row (treated as 0 in totals)</p>
        )}
        {!columns.length && <p className="cm-empty">Unknown: no observations for this market and date range.</p>}
      </div>
    </section>
  );
}
function ComparisonCharts({ state, onInspect }: { state: ReportState; onInspect: (x: Inspection) => void }) {
  const { chartSeries, metadata } = useContributionModel();
  const data = useMemo(() => chartSeries(state), [state, chartSeries]);
  return (
    <div className="cm-comparison-charts">
      <div className="cm-series-legend">
        <span>
          <i className="prior" />
          Previous Year · {Number(state.year) - 1}
        </span>
        <span>
          <i className="current" />
          Selected Year · {state.year}
        </span>
        {data.some((r) => (r as { growthScope?: string }).growthScope) && (
          <span title="The current month is incomplete, so its growth compares month to date with the same days last year.">
            Current month growth: MTD vs same days LY
          </span>
        )}
      </div>
      {[
        { title: 'Net CM', current: 'currentCm', prior: 'priorCm', growth: 'cmGrowth' },
        { title: 'GM AR', current: 'currentGm', prior: 'priorGm', growth: 'gmGrowth' },
      ].map((spec) => (
        <section className="cm-time-chart" key={spec.title}>
          <h2>{spec.title}</h2>
          <div className="cm-plot">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data} margin={{ top: 28, right: 16, bottom: 26, left: 8 }}>
                <CartesianGrid vertical={false} stroke="var(--cm-line)" />
                <XAxis
                  dataKey="label"
                  tick={{ fill: 'var(--cm-text)', fontSize: 10 }}
                  interval="preserveStartEnd"
                  minTickGap={16}
                  label={{
                    value: state.cadence,
                    position: 'bottom',
                    offset: 10,
                    fill: 'var(--cm-muted)',
                    fontSize: 10,
                  }}
                />
                <YAxis
                  tick={{ fill: 'var(--cm-text)', fontSize: 10 }}
                  tickFormatter={(v) =>
                    Math.abs(v) >= 1000000
                      ? number(v / 1000000, 0) + 'M'
                      : Math.abs(v) >= 1000
                        ? number(v / 1000, 0) + 'k'
                        : number(v)
                  }
                  width={55}
                  label={{ value: 'A$', angle: -90, position: 'insideLeft', fill: 'var(--cm-muted)', fontSize: 10 }}
                />
                <ReportChartTooltip
                  contentStyle={{
                    background: 'var(--cm-surface)',
                    color: 'var(--cm-text)',
                    border: '1px solid var(--cm-line)',
                  }}
                  formatter={(v) => displayMoney(v as Value)}
                />
                <Bar
                  dataKey={spec.current}
                  name={`Selected Year (${state.year})`}
                  fill="var(--cm-teal)"
                  isAnimationActive={false}
                  maxBarSize={64}
                  onClick={(_, index) => {
                    const r = data[index];
                    const values = r as unknown as Record<string, Value>;
                    const sameDays = spec.prior === 'priorCm' ? values.priorMtdCm : values.priorMtdGm;
                    onInspect({
                      title: `${spec.title} · ${r.label}`,
                      rows: [
                        ['Market', state.market],
                        ['Selected year', displayMoney(values[spec.current])],
                        ['Previous year', displayMoney(values[spec.prior])],
                        ...(values.growthScope
                          ? [['Previous year, same days', displayMoney(sameDays)] as [string, string]]
                          : []),
                        ['Growth', percent(values[spec.growth])],
                        [
                          'Scope',
                          values.growthScope
                            ? 'MTD vs same days LY: month to date against the same days last year (the full prior month is shown as the dashed line)'
                            : 'Full selected calendar year, capped at completed Sydney day; full prior calendar year',
                        ],
                        ['Source', metadata.source],
                      ],
                    });
                  }}
                >
                  <LabelList
                    dataKey={spec.growth}
                    position="top"
                    fill="var(--cm-text)"
                    fontSize={10}
                    formatter={(v) =>
                      v === null ? '' : `${Number(v) >= 0 ? '▲+' : '▼'}${number(Number(v) * 100, 1)}%`
                    }
                  />
                </Bar>
                <Line
                  dataKey={spec.prior}
                  name={`Previous Year (${Number(state.year) - 1})`}
                  stroke="var(--cm-prior)"
                  strokeWidth={3}
                  strokeDasharray="10 7"
                  dot={false}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </section>
      ))}
      <details className="cm-chart-values">
        <summary>Explore chart values · current, previous, growth and CM/GM growth index</summary>
        <div className="cm-matrix-scroll">
          <table className="cm-matrix">
            <thead>
              <tr>
                {[
                  'Period',
                  'Selected Net CM A$',
                  'Prior Net CM A$',
                  'CM growth',
                  'Selected GM AR A$',
                  'Prior GM AR A$',
                  'GM growth',
                  'CM / GM growth',
                ].map((x) => (
                  <th key={x}>{x}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((r) => (
                <tr key={r.period}>
                  <th>{r.label}</th>
                  <td>{displayMoney(r.currentCm)}</td>
                  <td>{displayMoney(r.priorCm)}</td>
                  <td>{percent(r.cmGrowth)}</td>
                  <td>{displayMoney(r.currentGm)}</td>
                  <td>{displayMoney(r.priorGm)}</td>
                  <td>{percent(r.gmGrowth)}</td>
                  <td>
                    {r.cmGrowth === null || r.gmGrowth === null || r.gmGrowth === 0
                      ? 'Unknown'
                      : number(r.cmGrowth / r.gmGrowth, 2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}
export default function ContributionMarginReport({
  nativeTheme,
  onContextChange,
  onAskRichard,
  state,
  onChange,
}: ContributionMarginReportProps) {
  const {
    health,
    metadata,
    overview,
    pageContext,
    reportCsv,
    reportDefaults,
    reportError,
    loading,
    exportRows,
    parseReport,
    legacyRows,
    previousMonth,
  } = useContributionModel();
  const setState = (next: ReportState | ((s: ReportState) => ReportState)) =>
    onChange(typeof next === 'function' ? next(state) : next);
  const [method, setMethod] = useState(false);
  const [inspection, setInspection] = useState<Inspection | null>(null);
  const root = useRef<HTMLDivElement>(null);
  const context = useMemo(
    () => ({
      ...pageContext(state),
      selection: inspection ? { title: inspection.title, ...Object.fromEntries(inspection.rows) } : undefined,
    }),
    [state, inspection, pageContext],
  );
  const error = reportError(state);
  const overviewRows = useMemo(() => (error ? [] : overview(state)), [state, error, overview]);
  const [start, end] = currentWindow(state),
    [priorStart, priorEnd] = comparisonWindow(state);
  const theme = nativeTheme ?? state.theme;
  function patch(p: Partial<ReportState>) {
    setState((s) => ({ ...s, ...p }));
  }
  useEffect(() => {
    onContextChange?.(context);
  }, [context, onContextChange]);
  function changeView(view: ReportView) {
    patch({
      view,
      ...(view === 'Comparision Chart' && ['Monthly - MTD', 'Yearly - YTD'].includes(state.cadence)
        ? { cadence: 'Monthly' as const }
        : {}),
    });
  }
  function exportFile() {
    if (error) {
      return;
    }
    const blob = new Blob([reportCsv(state)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contribution-margin-${state.view.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-${state.from}-${state.to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }
  const chartView = state.view === 'Comparision Chart';
  const compareView = state.view === 'Comparision Overview';
  const mtdView = state.view === 'MTD Expectation';
  const globalMonthly = state.view === 'Global Monthly CM Tracking';
  const regionalMonthly = state.view === 'Regions Monthly CM Tracking';
  const dailyView = state.view === 'Daily CM Tracking';
  const healthView = state.view === 'Source health';
  return (
    <div
      ref={root}
      className={'cm-fidelity ' + (state.filters === 'hide' ? 'filters-hidden' : '')}
      data-cm-theme={theme}
    >
      <header className="cm-report-header">
        <div>
          <h1>{viewTitles[state.view] ?? 'Contribution Margin Tracker'}</h1>
          {dailyView || globalMonthly || regionalMonthly ? (
            <p>BR = Before Refunds, AR = After Refunds</p>
          ) : chartView ? (
            <p>
              Year {state.year} v {Number(state.year) - 1}
            </p>
          ) : compareView ? (
            <p>
              <em>{state.master}</em> | {dateLabel(start)} – {dateLabel(end)} v {dateLabel(priorStart)} –{' '}
              {dateLabel(priorEnd)}
            </p>
          ) : (
            <p>Source through {dateLabel(metadata.to)}</p>
          )}
        </div>
        <div className="cm-report-tools">
          <button onClick={() => patch({ filters: state.filters === 'show' ? 'hide' : 'show' })}>
            {state.filters === 'show' ? 'Hide filters' : 'Show filters'}
          </button>
          <button onClick={() => patch({ ...reportDefaults, view: state.view, theme: state.theme })}>
            Reset filters
          </button>
          <button onClick={() => setMethod(true)}>Method and sources</button>
          <FinancialActions
            ready={!error}
            rows={exportRows()}
            title={viewTitles[state.view]}
            source={metadata.source}
            range={`${state.from} – ${state.to}`}
            search={location.search}
            onRestore={(search) => onChange(parseReport(search, 'cm'))}
          />
          <button onClick={exportFile} disabled={!!error}>
            Export CSV
          </button>
          {onAskRichard && <button onClick={() => onAskRichard(context)}>Ask Richard</button>}
        </div>
      </header>
      <nav className="cm-report-tabs" role="tablist" aria-label="Contribution Margin workbook views">
        {reportViews
          .filter((v) => v !== 'Sheet 68 · legacy bank fees')
          .map((v) => (
            <button
              key={v}
              role="tab"
              aria-selected={state.view === v}
              aria-current={state.view === v ? 'page' : undefined}
              onClick={() => changeView(v)}
            >
              {v.replace('Comparision', 'Comparison')}
            </button>
          ))}
      </nav>
      <div className="cm-active-scope" aria-label="Active filters">
        <span>{globalMonthly ? 'Global - All Markets' : state.market}</span>
        <span>
          {chartView
            ? `Calendar ${state.year} v ${Number(state.year) - 1}`
            : mtdView
              ? `Projected month ${metadata.to.slice(0, 7)}`
              : `${compareView ? start : state.from} – ${compareView ? end : state.to}`}
        </span>
        <span>{compareView ? state.basis : 'A$ · BR / AR'}</span>
        <span>Source through {metadata.to}</span>
      </div>
      <div className="cm-workbook">
        <main className="cm-report-canvas" aria-label={state.view}>
          {error && loading ? (
            <div className="cm-empty" role="status">
              <p>{error}</p>
            </div>
          ) : error ? (
            <div className="cm-empty" role="alert">
              <h2>Unknown</h2>
              <p>{error}</p>
              <button onClick={() => setState(reportDefaults)}>Reset filters</button>
            </div>
          ) : (
            <>
              {state.view === 'Sheet 68 · legacy bank fees' ? (
                <>
                  <SelectField
                    label="Legacy bank-fee region (independent calculation parameter)"
                    value={state.legacyRegion}
                    values={reportMarkets}
                    onChange={(legacyRegion) => patch({ legacyRegion })}
                  />
                  <FinancialMatrix
                    rows={legacyRows()}
                    dateKey="transaction_date_sydt"
                    metrics={[
                      'bank_fees_legacy_old',
                      'bank_fees_legacy_copy',
                      'bank_fees',
                      'bank_fees_mtd',
                      'commission_mtd',
                      'net_cm',
                      'comparison_cm',
                      'gross_cm',
                    ]}
                    onInspect={(row) =>
                      setInspection({
                        title: 'Sheet 68 source operands',
                        rows: Object.entries(row).map(([key, value]) => [key, String(value ?? 'Unknown')]),
                      })
                    }
                  />
                </>
              ) : compareView ? (
                <div className="cm-overview-grid">
                  {overviewRows.map((r) => (
                    <section
                      className={'cm-market-pair ' + (r.market === 'Global - All Markets' ? 'is-global' : '')}
                      key={r.market}
                    >
                      <div className="cm-paired-metrics">
                        <div>
                          <h2>
                            {r.market === 'Global - All Markets' ? 'Global' : r.market} {state.basis}
                          </h2>
                          <button
                            className="cm-metric-value"
                            title={`Previous: ${displayMoney(r.previous)}. Click to inspect.`}
                            onClick={() =>
                              setInspection({
                                title: `${r.market} ${state.basis}`,
                                rows: [
                                  ['Current', displayMoney(r.current)],
                                  ['Previous', displayMoney(r.previous)],
                                  ['Growth', percent(r.cmGrowth)],
                                  ['CM growth / GM growth', r.index === null ? 'Unknown' : number(r.index, 2)],
                                  ['Dates', `${start} – ${end}`],
                                  ['Prior dates', `${priorStart} – ${priorEnd}`],
                                  [
                                    'Definition',
                                    state.basis === 'Net CM'
                                      ? 'Comparison Net CM excludes interest income and VCC rebate.'
                                      : 'GM (AR) less total marketing spend.',
                                  ],
                                ],
                              })
                            }
                          >
                            {displayMoney(r.current)}
                          </button>
                          <Growth value={r.cmGrowth} />
                        </div>
                        <div>
                          <h2>{r.market === 'Global - All Markets' ? 'Global' : r.market} GM (AR)</h2>
                          <button
                            className="cm-metric-value"
                            title={`Previous: ${displayMoney(r.priorGm)}. Click to inspect.`}
                            onClick={() =>
                              setInspection({
                                title: `${r.market} GM (AR)`,
                                rows: [
                                  ['Current', displayMoney(r.gm)],
                                  ['Previous', displayMoney(r.priorGm)],
                                  ['Growth', percent(r.gmGrowth)],
                                  ['Source', metadata.source],
                                ],
                              })
                            }
                          >
                            {displayMoney(r.gm)}
                          </button>
                          <Growth value={r.gmGrowth} />
                        </div>
                      </div>
                      <button
                        className="cm-market-detail"
                        onClick={() =>
                          patch({
                            view: 'Regions Monthly CM Tracking',
                            market: r.market,
                            from: start,
                            to: end > metadata.to ? metadata.to : end,
                            master: 'Custom Date Range',
                          })
                        }
                      >
                        Monthly detail <span aria-hidden="true">↗</span>
                      </button>
                    </section>
                  ))}
                </div>
              ) : chartView ? (
                <ComparisonCharts state={state} onInspect={setInspection} />
              ) : healthView ? (
                <section className="cm-matrix-section">
                  <h2>Observed marketing inputs</h2>
                  <div className="cm-matrix-scroll">
                    <table className="cm-matrix cm-health-table">
                      <thead>
                        <tr>
                          {[
                            'Market',
                            'Channel',
                            'First date',
                            'Last date',
                            'Dates present',
                            'Zero-cost dates',
                            'Cost (A$)',
                          ].map((c) => (
                            <th key={c}>{c}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {health({
                          view: 'Source health',
                          from: state.from,
                          to: state.to,
                          market: state.market === 'Global - All Markets' ? 'All markets' : state.market,
                          basis: state.basis,
                          layout: 'standard',
                          theme,
                          comparison: 'Previous year',
                        }).map((r) => (
                          <tr key={r.market + r.channel}>
                            <th>{r.market}</th>
                            <td>{r.channel.replace('Markeing', 'Marketing')}</td>
                            <td>{r.first}</td>
                            <td>{r.last}</td>
                            <td>{r.days ?? 'Unknown'}</td>
                            <td>{r.zeros ?? 'Unknown'}</td>
                            <td>{number(r.cost, 2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              ) : mtdView ? (
                <div className="cm-mtd-pair">
                  <Matrix state={state} market="Global - All Markets" projection onInspect={setInspection} />
                  <Matrix
                    state={state}
                    market={state.market === 'Global - All Markets' ? 'AU' : state.market}
                    projection
                    onInspect={setInspection}
                  />
                </div>
              ) : regionalMonthly ? (
                <div className="cm-regional-matrices">
                  {(state.market === 'Global - All Markets'
                    ? focusMarkets.filter((m) => m !== 'Global - All Markets')
                    : [state.market]
                  ).map((market) => (
                    <Matrix key={market} state={state} market={market} onInspect={setInspection} />
                  ))}
                </div>
              ) : (
                <Matrix
                  state={state}
                  market={globalMonthly ? 'Global - All Markets' : state.market}
                  onInspect={setInspection}
                />
              )}
            </>
          )}
        </main>
        {state.filters === 'show' && (
          <aside className="cm-global-filters" aria-label="Global Filters">
            <p className="cm-filters-caption">Global Filters</p>
            {!compareView && !chartView && !mtdView && (
              <SelectField
                label="Transaction date (Sydney)"
                value={
                  state.from === reportDefaults.from && state.to === metadata.to
                    ? 'Last 6 months'
                    : state.from === metadata.to.slice(0, 7) + '-01' && state.to === metadata.to
                      ? 'Current month'
                      : state.from === previousMonth.from && state.to === previousMonth.to
                        ? 'Previous month'
                        : 'Custom range'
                }
                values={['Last 6 months', 'Current month', 'Previous month', 'Custom range']}
                onChange={(v) =>
                  v === 'Last 6 months'
                    ? patch({ from: reportDefaults.from, to: metadata.to })
                    : v === 'Current month'
                      ? patch({ from: metadata.to.slice(0, 7) + '-01', to: metadata.to })
                      : v === 'Previous month'
                        ? patch(previousMonth)
                        : undefined
                }
              />
            )}

            <h2>Date</h2>
            {chartView ? (
              <>
                <SelectField
                  label="Year"
                  value={state.year}
                  values={['2023', '2024', '2025', '2026']}
                  onChange={(year) => patch({ year })}
                />
                <SelectField
                  label="P. Date Cadence (for chart only)"
                  value={state.cadence}
                  values={['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Yearly']}
                  onChange={(v) => patch({ cadence: v as ReportState['cadence'] })}
                />
                <details className="cm-legacy-controls">
                  <summary>Original date parameters</summary>
                  <p className="cm-small-note">
                    Retained workbook parameters. The original year-series calculations use Year, not these
                    date/comparison inputs.
                  </p>
                  <label className="cm-field">
                    <span>P. Start Date</span>
                    <input type="date" value={state.from} onChange={(e) => patch({ from: e.target.value })} />
                  </label>
                  <label className="cm-field">
                    <span>P. End Date</span>
                    <input type="date" value={state.to} onChange={(e) => patch({ to: e.target.value })} />
                  </label>
                  <SelectField
                    label="P. Date Comparison Period"
                    value={state.comparison}
                    values={['Previous Year', 'Previous Period', 'Previous Month', 'Previous Quarter']}
                    onChange={(v) => patch({ comparison: v as ReportState['comparison'] })}
                  />
                </details>
                <p className="cm-small-note">
                  The 7.1 source Chart shelves group by month for every retained cadence parameter value. Date Selection
                  worksheets use the cadence control. Charts use the selected calendar year and full previous year.
                  Current values stop at the completed Sydney day.
                </p>
              </>
            ) : mtdView ? (
              <p className="cm-small-note">
                {mtdNote(metadata.to)} GM and ROAS ratios are unchanged; Refunds % is projected refunds ÷ projected
                gross sales (BR). This is a projection, not an actual.
              </p>
            ) : (
              <>
                {compareView && (
                  <SelectField
                    label="Master Date"
                    value={state.master}
                    values={['Custom Date Range', 'Date Selection']}
                    onChange={(v) => patch({ master: v as ReportState['master'] })}
                  />
                )}{' '}
                {!compareView || state.master === 'Custom Date Range' ? (
                  <div className="cm-date-pair">
                    <label className="cm-field">
                      <span>P. Start Date</span>
                      <input
                        type="date"
                        value={state.from}
                        max={metadata.to}
                        onChange={(e) => patch({ from: e.target.value })}
                      />
                    </label>
                    <label className="cm-field">
                      <span>P. End Date</span>
                      <input
                        type="date"
                        value={state.to}
                        max={metadata.to}
                        onChange={(e) => patch({ to: e.target.value })}
                      />
                    </label>
                  </div>
                ) : (
                  <>
                    <label className="cm-field">
                      <span>P. Date Selection</span>
                      <input
                        type="date"
                        value={state.selectedDate}
                        onChange={(e) => patch({ selectedDate: e.target.value })}
                      />
                    </label>
                    <SelectField
                      label="P. Date Cadence"
                      value={state.cadence}
                      values={['Daily', 'Weekly', 'Monthly', 'Monthly - MTD', 'Quarterly', 'Yearly', 'Yearly - YTD']}
                      onChange={(v) =>
                        patch({ cadence: v as ReportState['cadence'], selectionComparison: selectionComparisons[v][0] })
                      }
                    />
                  </>
                )}
                {compareView && (
                  <SelectField
                    label="P. Date Comparison Period"
                    value={state.master === 'Date Selection' ? state.selectionComparison : state.comparison}
                    values={
                      state.master === 'Date Selection'
                        ? Object.values(selectionComparisons).flat()
                        : ['Previous Year', 'Previous Period', 'Previous Month', 'Previous Quarter']
                    }
                    onChange={(v) =>
                      state.master === 'Date Selection'
                        ? patch({ selectionComparison: v })
                        : patch({ comparison: v as ReportState['comparison'] })
                    }
                  />
                )}
              </>
            )}
            {!globalMonthly && (
              <>
                <h2>Location</h2>
                <SelectField
                  label="Select Regions"
                  value={state.market}
                  values={reportMarkets.includes(state.market) ? reportMarkets : [...reportMarkets, state.market]}
                  onChange={(market) => patch({ market })}
                />
              </>
            )}
            {compareView && (
              <>
                <h2>Margin</h2>
                <SelectField
                  label="Select Margin"
                  value={state.basis}
                  values={['Net CM', 'Gross CM']}
                  onChange={(basis) => patch({ basis: basis as ReportState['basis'] })}
                />
                <SelectField
                  label="Market order"
                  value={state.sort}
                  values={['Workbook order', 'GM size']}
                  onChange={(sort) => patch({ sort: sort as ReportState['sort'] })}
                />
                <div className="cm-growth-legend">
                  <b>CM / GM (% Change)</b>
                  <span>▲ Increase · ▼ Decrease</span>
                  <span>Unknown = unavailable / zero base</span>
                </div>
              </>
            )}
            {(globalMonthly || regionalMonthly) && (
              <div className="cm-small-note cm-target-note">
                Monthly targets use the governed CM source. Months and regions absent from that source read Unknown.
              </div>
            )}
            <div className="cm-data-note">
              <b>Live scope · {dateLabel(metadata.to)}</b>
              <span>Manual spend completeness and Tableau refresh equivalence are unconfirmed.</span>
              <button onClick={() => patch({ view: 'Source health' })}>Inspect source coverage</button>
            </div>
          </aside>
        )}
      </div>
      <div className="cm-source-line">
        analytics.cm_with_marketing_costs · Extracted {metadata.extractedAt.slice(0, 10)} · Governed live source ·{' '}
        {chartView
          ? 'Selected-year bars v previous-year dashed line'
          : compareView
            ? 'Comparison Net CM excludes finance additions'
            : mtdView
              ? 'Linear month expectation; full-month governed target'
              : 'Matrix Net CM includes interest income and VCC rebate'}
        .
      </div>
      {(method || inspection) && (
        <div
          className="cm-dialog-backdrop"
          onClick={() => {
            setMethod(false);
            setInspection(null);
          }}
        >
          <section
            className="cm-dialog"
            role="dialog"
            aria-modal="true"
            aria-label={inspection?.title ?? 'Method and sources'}
            onClick={(e) => e.stopPropagation()}
          >
            <header>
              <h2>{inspection?.title ?? 'Method and sources'}</h2>
              <button
                onClick={() => {
                  setMethod(false);
                  setInspection(null);
                }}
                aria-label="Close details"
              >
                ×
              </button>
            </header>
            {inspection ? (
              <>
                <dl>
                  {inspection.rows.map(([k, v]) => (
                    <React.Fragment key={k}>
                      <dt>{k}</dt>
                      <dd>{v}</dd>
                    </React.Fragment>
                  ))}
                </dl>
                {onAskRichard && <button onClick={() => onAskRichard(context)}>Ask Richard about this scope</button>}
              </>
            ) : (
              <>
                <h3>Original report structure</h3>
                <p>
                  Based on published workbook revision 7.1 and the corrected 13 October overview/chart screenshots.
                  Daily and monthly matrices retain the original manual metric order; the overview retains paired CM/GM
                  market positions. Teal replaces Tableau blue, with a dashed teal-tint prior-year line.
                </p>
                <h3>Definitions</h3>
                <p>
                  Gross CM = GM after refunds − marketing costs. Comparison Net CM also subtracts bank fees, commissions
                  and customer discounts. Daily/monthly Net CM adds interest income and VCC rebate. Commissions are
                  0.77% of TTV after refunds. Region bank-fee rates and US + CA grouping follow the workbook. No new
                  refund, FX or cost-allocation definition is applied.
                </p>
                <p>
                  Growth = (current − previous) / |previous|. Zero or unavailable bases read Unknown. The source’s
                  percentage format is preserved for ROAS. Row shading indicates magnitude within a metric; it is not a
                  good/bad judgement.
                </p>
                <h3>Data coverage</h3>
                <p>
                  Source: {metadata.source}. Sydney transaction dates {metadata.from} to {metadata.to}. Table updated{' '}
                  {metadata.tableModifiedAt}; extracted {metadata.extractedAt}. Future input dates are excluded.
                  Manual-sheet formulas match the warehouse; that does not certify entered-spend completeness.
                </p>
                <h3>Known differences</h3>
                <p>
                  Monthly and MTD targets use the Finance FY27 CM Budget (Group tab CM Target, as in Tableau). Missing
                  source months and regions remain Unknown; the workbook defines no daily target. Marketing channels
                  absent in the source read Unknown. Customer counts are source daily distinct counts; do not interpret
                  a multi-day sum as period-distinct customers. The source overview’s Gross CM parameter and margin
                  calculation have an inconsistent legacy label; this page uses the separately verified Gross CM formula
                  and does not claim exact selector parity.
                </p>
                <p>
                  Chart cadence choices use the verified source dates; the source screenshot directly verifies the
                  Monthly view. Hidden prototype Dashboard actions, every original cadence combination and source
                  refresh are unverified. This is a scoped implementation, not full Tableau parity.
                </p>
                <h3>Sources</h3>
                <p>
                  Tableau: DailyCMTracking, GlobalMonthlyCMTracking, RegionsMonthlyCMTracking, ComparisionOverview and
                  ComparisionChart. Slack correction: 12–13 October 2026; daily ordering: 20 August 2025. Manual input
                  sheet: Daily CM tracker. Source evidence and queries are retained locally.
                </p>
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
